import asbestosProjectService from "@/views/asbestosProjects/asbestosProjectService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";

import type { AsbestosProject, AsbestosProjectNew, AsbestosProjectQuery } from "./asbestosProject";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "asbestos-projects";
const ENTITY_NAME = "Asbestos Project";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<AsbestosProject>(apiHosts.AQMS, axiosParams);

const getAsbestosProjects = (params: AsbestosProjectQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getAsbestosProjectById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewAsbestosProject = async (ids: AsbestosProjectIds) => {
  const defaultApp = {
    data: {
      ...asbestosProjectService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<AsbestosProjectNew, any>;

  return defaultApp;
};

type AsbestosProjectIds = {
  id?: string | number;
};
const getAsbestosProjectByIdOrNew = (ids: AsbestosProjectIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewAsbestosProject(ids);
  } else {
    return getAsbestosProjectById(ids.id, config) as Promise<AxiosResponse<AsbestosProjectNew, any>>;
  }
};

const createAsbestosProject = (asbestosProject: AsbestosProject, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, asbestosProject, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateAsbestosProject = (asbestosProject: AsbestosProject, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + asbestosProject.id, asbestosProject, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {AsbestosProject} asbestosProject This cannot be a 'ref' to a asbestosProject
 */
const saveAsbestosProject = (asbestosProject: AsbestosProject, config = {}) => {
  if (asbestosProject.id) {
    return updateAsbestosProject(asbestosProject, config);
  } else {
    return createAsbestosProject(asbestosProject, config);
  }
};

const deleteAsbestosProjectById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteAsbestosProjectById,
  getAsbestosProjects,
  getAsbestosProjectById,
  getNewAsbestosProject,
  getAsbestosProjectByIdOrNew,
  createAsbestosProject,
  updateAsbestosProject,
  saveAsbestosProject,
};
